import { InsightsReportsRoute } from './createRoute'
import { getSubdomain } from '@/auth'
import {
  createRouter as _createRouter,
  createWebHistory,
  RouteRecordRaw,
  START_LOCATION,
} from 'vue-router'
import _MyReports from '@/views/_MyReports.vue'
import Forbidden403 from '@/views/403_Forbidden.vue'
import { getCompany, getUser } from '@/api/api.portal'
import {
  getUserTier,
  isCompanyTokenValid,
  tierCanAccess,
  tierLanding,
} from '@/models/model.insights'
import {
  isDashboardsSurveysEnabled,
  fastExamDashboardEnabled,
  powerBiDashboardRedirectEnabled,
} from '@/unleashClient'
import { getUserTokenInfo } from '@/auth'
import * as Sentry from '@sentry/vue'
import { NEW_INSIGHTS_URL } from '@/consts'
import { useCreateExternalLink } from '@/use/use.createExternalLink'
import {
  NEW_INSIGHTS_LANDING_PATH,
  shouldRedirectToNewInsights,
} from '@/helpers/helper.powerBiRedirect'

const _Home = () => import('@/views/_Home.vue')
const _Ask = () => import('@/views/_Ask.vue')
const _Settings = () => import('@/views/_Settings.vue')
const _MyReports_Wizard = () => import('@/views/_MyReports_Wizard.vue')
const _MyReports_Wizard_Config = () =>
  import('@/views/_MyReports_Wizard_Config.vue')
const _MyReports_Wizard_Type = () =>
  import('@/views/_MyReports_Wizard_Type.vue')
const _MyReports_Wizard_Filters = () =>
  import('@/views/_MyReports_Wizard_Filters.vue')
const _MyReports_Wizard_Review = () =>
  import('@/views/_MyReports_Wizard_Review.vue')
const _MyReports_Edit = () => import('@/views/_MyReports_Edit.vue')
const _MyReports_Download = () => import('@/views/_MyReports_Download.vue')
const _MyDashboard = () => import('@/views/_Dashboard.vue')
const _MySurveysProducts = () => import('@/views/_Surveys_Dashboard.vue')
const _MySurveysModules = () => import('@/views/_Surveys_Dashboard_Module.vue')
const _MySurveysSummary = () => import('@/views/_Surveys_Summary.vue')
const _TrainingImpact = () => import('@/views/_TrainingImpactDashboard.vue')
const _TrainingDashboardProducts = () =>
  import('@/views/_Training_Dashboard_Products.vue')
const _RealTalkDashboard = () => import('@/views/_RealTalkDashboard.vue')
const _FastExamDashboard = () => import('@/views/_Fast_Exam_Dashboard.vue')
const _FastExamDashboardProducts = () =>
  import('@/views/_Fast_Exam_Dashboard_Products.vue')

const routes: RouteRecordRaw[] = [
  {
    component: _Home,
    name: InsightsReportsRoute.HOME,
    path: '/',
    meta: { access: 'full' },
  },
  {
    path: '/schedules',
    name: 'MySchedules',
    component: () => import('@/views/Schedules/MySchedules.vue'),
    meta: { access: 'full' },
  },
  {
    path: `/${InsightsReportsRoute.TRAINING_IMPACT}/:productId/:productName/:productType`,
    name: InsightsReportsRoute.TRAINING_IMPACT,
    component: _TrainingImpact,
    meta: { access: 'full' },
  },
  {
    path: `/${InsightsReportsRoute.REAL_TALK_DASHBOARD}/:productId/:moduleId/:productName/:productType`,
    name: InsightsReportsRoute.REAL_TALK_DASHBOARD,
    component: _RealTalkDashboard,
    meta: { access: 'full' },
  },
  {
    path: `/${InsightsReportsRoute.TRAINING_IMAPCT_PRODUCTS}`,
    name: InsightsReportsRoute.TRAINING_IMAPCT_PRODUCTS,
    component: _TrainingDashboardProducts,
    meta: { access: 'full' },
  },
  {
    path: '/schedules/new',
    name: 'CreateSchedule',
    component: () => import('@/views/Schedules/Create.vue'),
    meta: { access: 'full' },
  },
  {
    path: '/schedules/edit/:scheduleId',
    name: 'EditSchedule',
    component: () => import('@/views/Schedules/Edit.vue'),
    meta: { access: 'full' },
  },
  {
    component: _MyReports,
    name: InsightsReportsRoute.MY_REPORTS,
    path: '/reports',
    meta: { access: 'full' },
  },
  {
    component: _MyDashboard,
    name: InsightsReportsRoute.MY_DASHBOARD,
    path: '/dashboard',
    meta: { access: 'restricted' },
  },
  {
    component: _Settings,
    name: InsightsReportsRoute.SETTINGS,
    path: '/settings',
    meta: { access: 'full' },
  },
  {
    path: '/reports/new/',
    component: _MyReports_Wizard,
    props: true,
    meta: { access: 'full' },
    children: [
      {
        component: _MyReports_Wizard_Config,
        name: InsightsReportsRoute.MY_REPORTS_WIZARD_CONFIG,
        path: InsightsReportsRoute.MY_REPORTS_WIZARD_CONFIG,
      },
      {
        component: _MyReports_Wizard_Type,
        name: InsightsReportsRoute.MY_REPORTS_WIZARD_TYPE,
        path: InsightsReportsRoute.MY_REPORTS_WIZARD_TYPE,
      },
      {
        component: _MyReports_Wizard_Filters,
        name: InsightsReportsRoute.MY_REPORTS_WIZARD_FILTERS,
        path: InsightsReportsRoute.MY_REPORTS_WIZARD_FILTERS,
      },
      {
        component: _MyReports_Wizard_Review,
        name: InsightsReportsRoute.MY_REPORTS_WIZARD_REVIEW,
        path: InsightsReportsRoute.MY_REPORTS_WIZARD_REVIEW,
      },
    ],
  },
  {
    path: '/reports/edit/:reportId',
    name: 'EditReport',
    component: _MyReports_Edit,
    meta: { access: 'full' },
  },
  {
    path: '/reports/download/:reportId',
    name: 'DownloadReport',
    component: _MyReports_Download,
    meta: { access: 'full' },
  },
  {
    component: Forbidden403,
    name: '403 Forbidden',
    path: '/403',
    meta: { access: 'public' },
  },
]

const createRouter = async () => {
  // Independent startup fetches — run them concurrently rather than serially.
  const [company, user, tokenInfo, subdomain] = await Promise.all([
    getCompany(),
    getUser(),
    getUserTokenInfo(),
    getSubdomain(),
  ])

  // Access is resolved entirely from these startup fetches. If the company, user
  // profile, or token failed to load (e.g. a transient network/auth blip returning an
  // empty body), surface it as a startup error — caught in main.ts and reported to
  // Sentry, then recovered by a reload. Falling through would treat a possibly-valid
  // superuser as the lowest tier and lock them on /403 for the whole session with no
  // in-session recovery, indistinguishable from a genuinely role-less user.
  if (!company || !user || !tokenInfo) {
    throw new Error(
      'Router access control could not resolve company/user/token on startup'
    )
  }

  const isAskAvailable = company.feature_flags?.includes('insights_ask')

  if (isDashboardsSurveysEnabled()) {
    routes.unshift(
      {
        component: _MySurveysProducts,
        name: InsightsReportsRoute.MY_SURVEY_PRODUCTS,
        path: '/survey-products/:isTrainingImpact',
        meta: { access: 'full' },
      },
      {
        component: _MySurveysModules,
        name: InsightsReportsRoute.MY_SURVEYS_SURVEY,
        path: '/survey-type/:productId/:productType/:isTrainingImpact',
        meta: { access: 'full' },
      },
      {
        component: _MySurveysSummary,
        name: InsightsReportsRoute.MY_SURVEYS_SUMMARY,
        path: '/survey-summary/:surveyId/:productId/:productType/:isTrainingImpact',
        meta: { access: 'full' },
      }
    )
  }

  if (fastExamDashboardEnabled()) {
    routes.unshift({
      component: _FastExamDashboard,
      name: InsightsReportsRoute.FAST_EXAM_DASHBOARD,
      path: `/${InsightsReportsRoute.FAST_EXAM_DASHBOARD}/:productId`,
      meta: { access: 'full' },
    })
    routes.unshift({
      component: _FastExamDashboardProducts,
      name: InsightsReportsRoute.FAST_EXAM_DASHBOARD_PRODUCTS,
      path: `/${InsightsReportsRoute.FAST_EXAM_DASHBOARD_PRODUCTS}`,
      meta: { access: 'full' },
    })
  }

  if (isAskAvailable) {
    routes.unshift({
      component: _Ask,
      name: InsightsReportsRoute.ASK,
      path: '/ask',
      meta: { access: 'full' },
    })
  }

  // The user's JWT must belong to the company they are viewing (cross-tenant guard).
  // A mismatch is treated as the lowest tier so it flows through the same rules
  // below (landing = /403, every non-public route denied) — no special-casing.
  const companyMismatch = !isCompanyTokenValid(
    company.name_key,
    tokenInfo.companyNameKey
  )
  if (companyMismatch) {
    console.error('company name key invalid')
  }
  const userTier = companyMismatch ? 'none' : getUserTier(user?.roles)
  const landing = tierLanding(userTier)

  const router = _createRouter({
    history: createWebHistory(subdomain),
    routes,
  })

  // Single authoritative access guard. Every route declares the tier it needs
  // via meta.access (default-deny: a route with no meta is treated as 'full').
  // A user who lacks the required tier is redirected to their own landing page,
  // so the rules apply uniformly to every entry point — direct URL, the logo
  // link, or in-app navigation. This is the only place access is enforced.
  router.beforeEach((to) => {
    const required = to.meta.access ?? 'full'
    if (tierCanAccess(userTier, required)) {
      return true
    }

    // Defensive: never redirect a path to itself (avoids a loop if a landing
    // route is ever mis-tagged).
    return to.path === landing ? true : landing
  })

  // INSI-2261: from 5 October 2026 the Completion Dashboard tab lives in the
  // new Insights app. Decided here, before anything renders, so a direct hit on
  // /dashboard leaves without drawing the page. A global guard rather than
  // beforeEnter on the route because it must also fire on query-only changes
  // (/dashboard?powerbi=1 back to /dashboard). Registered after the access
  // guard, so users who may not open the dashboard never reach it.
  //
  // When we do leave, the returned promise never settles: the browser is
  // already navigating away, and settling it with `false` would reject
  // router.isReady() on a direct hit and log a startup error for a redirect
  // that worked. Every failure falls back to rendering PowerBI.
  //
  // Minting the transfer key takes a moment, during which the user may click
  // on (or double-click the tab). Each navigation through this guard bumps
  // the counter, and only the latest one is allowed to leave.
  let navigationSeq = 0
  router.beforeEach(async (to, from) => {
    const thisNavigation = ++navigationSeq
    if (to.name !== InsightsReportsRoute.MY_DASHBOARD) return true

    const redirect = shouldRedirectToNewInsights({
      flagEnabled: powerBiDashboardRedirectEnabled(),
      query: to.query,
      newInsightsUrl: NEW_INSIGHTS_URL,
    })
    if (!redirect) return true

    try {
      const url = await useCreateExternalLink(
        NEW_INSIGHTS_URL,
        'new-insights',
        NEW_INSIGHTS_LANDING_PATH
      )
      if (thisNavigation !== navigationSeq) return false

      // A direct hit, or the login landing chain, has no in-app page behind it:
      // the document is still at this URL because the navigation never
      // finalised, so `assign` would leave a history entry that redirects
      // forward again on Back. Replace it. In-app clicks keep `assign`, since
      // the document is still at the previous page and Back should return there.
      if (from === START_LOCATION) {
        window.location.replace(url)
      } else {
        window.location.assign(url)
      }
      return new Promise<boolean>(() => undefined)
    } catch (error) {
      // Sentry may not be initialised yet on a direct hit; keep the console line.
      console.error('Redirect to the new Insights dashboard failed', error)
      Sentry.captureException(error)
      return true
    }
  })

  return router
}

export const router = createRouter()
